import ErrorComponent from "@/components/atoms/ErrorComponent";
import RecordCallLogsCard from "@/components/molecules/cards/callLogs/RecordCallLogsCard";
import InstructorHeader from "@/components/molecules/card-headers/instructors/InstructorHeader";
import InstructorDetailsCard from "@/components/molecules/cards/instructors/InstructorDetailsCard";
import InstructorNotesCard from "@/components/molecules/cards/instructors/InstructorNotesCard";
import InstructorCourses from "@/components/molecules/cards/instructors/InstructorCourses";
import { UUID_REGEX } from "@/constants/regexs";
import { pluralize } from "@/helpers/utils";
import Instructor from "@/interfaces/Instructor";
import {
  faComment,
  faFile,
  faPhone,
  faUser,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useState } from "react";
import { Card, Col, Nav, Row, Tab } from "react-bootstrap";
import { useParams } from "react-router-dom";
import SimpleBar from "simplebar-react";

const InstructorView: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [instructor, setInstructor] = useState<Instructor>();
  const [totals, setTotals] = useState({
    courses: 0,
    learners: 0,
    companies: 0,
    notes: 0,
    calls: 0,
  });

  const onGetTotal = (key: string, total: number) => {
    setTotals((prev) => ({ ...prev, [key]: total }));
  };

  if (!id || !UUID_REGEX.test(id)) {
    return (
      <ErrorComponent error={{ status: 404, data: {} }} absolute returnButton />
    );
  }

  return (
    <div className="formalys-page formalys-detail-page">
      {instructor && <InstructorHeader id={id} />}
      <Row className="g-3">
        <Col xxl={4} xl={5}>
          <InstructorDetailsCard id={id} card callBack={setInstructor} />
        </Col>
        {instructor && (
          <Col xxl={8} xl={7}>
            <Card>
              <Tab.Container defaultActiveKey="companies">
                <SimpleBar>
                  <Card.Header className="p-0 border-bottom">
                    <Nav className="nav-tabs border-0 flex-nowrap tab-contact-details">
                      <Nav.Item>
                        <Nav.Link
                          eventKey="companies"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faUser}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            {pluralize("Entreprise", totals.companies)}
                            &nbsp;(
                            {totals.companies})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="learners"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faUsers}
                            className="text-600 tab-icon"
                          />

                          <h6 className="text-600 mb-0 ms-1">
                            {pluralize("Apprenant", totals.learners)}
                            &nbsp;(
                            {totals.learners})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          eventKey="courses"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faFile}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            {pluralize("Formation", instructor.courses?.length ?? 0)}
                            &nbsp;(
                            {instructor.courses?.length ?? 0})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>

                      <Nav.Item>
                        <Nav.Link
                          eventKey="notes"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faComment}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            {pluralize("Note", totals.notes)}&nbsp;(
                            {totals.notes})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>

                      <Nav.Item>
                        <Nav.Link
                          eventKey="calls"
                          className="d-flex align-items-center py-3 px-x1 mb-0"
                        >
                          <FontAwesomeIcon
                            icon={faPhone}
                            className="text-600 tab-icon"
                          />
                          <h6 className="text-600 mb-0 ms-1">
                            {pluralize("Appel", totals.calls)}&nbsp;(
                            {totals.calls})
                          </h6>
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </Card.Header>
                </SimpleBar>
                <Card.Body className="bg-light">
                  <Tab.Content>
                    <Tab.Pane eventKey="courses">
                      <InstructorCourses instructor={instructor} />
                    </Tab.Pane>
                    <Tab.Pane eventKey="notes">
                      <InstructorNotesCard
                        instructorId={id}
                        onGetTotal={(total) => onGetTotal("notes", total)}
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="calls">
                      <RecordCallLogsCard
                        instructorId={id}
                        onGetTotal={(total) => onGetTotal("calls", total)}
                      />
                    </Tab.Pane>
                  </Tab.Content>
                </Card.Body>
              </Tab.Container>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default InstructorView;
